import type { FC } from 'react';
import { useNavigate } from 'react-router';
import { ownerContributorAndViewerIds } from 'src/components/form';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import { useInsertOversightAssessment } from 'src/hooks/mutations/oversight-assessment';
import type { OversightAssessmentFormDataFields } from 'src/pages/assessments/oversight-assessments/forms/oversight-assessment-form/oversightAssessmentSchema';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsFormPrefillEnabled } from '@/hooks/useIsFormPrefillEnabled';
import { oversightAssessmentDetailsUrl } from '@/utils/urls';

import OversightAssessmentForm from '../../forms/oversight-assessment-form';

const OversightAssessmentCreateTab: FC = () => {
  const navigate = useNavigate();
  const { insertOversightAssessment } = useInsertOversightAssessment();
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const formPrefillEnabled = useIsFormPrefillEnabled();

  const onSave = async (variables: OversightAssessmentFormDataFields) => {
    const data = await insertOversightAssessment({
      CustomAttributeData: variables.CustomAttributeData || undefined,
      ...ownerContributorAndViewerIds(variables),
      TagTypeIds: variables.tags?.map((t) => t.TagTypeId) || [],
      DepartmentTypeIds:
        variables.departments?.map((d) => d.DepartmentTypeId) || [],
      CompletedByUser: variables.CompletedByUser?.value ?? null,
      Status: variables.Status,
      StartDate: variables.StartDate,
      Title: variables.Title,
      Summary: variables.Summary,
      ActualCompletionDate: variables.ActualCompletionDate,
      NextTestDate: variables.NextTestDate,
      TargetCompletionDate: variables.TargetCompletionDate,
      Outcome: variables.Outcome,
      OriginatingItemId: null,
    });
    const assessmentId = data?.insertOversightAssessmentApi?.Id;
    if (!assessmentId) {
      throw new Error('Missing oversight assessment id');
    }

    return {
      savedIds: [assessmentId],
      createdEntity: {
        id: assessmentId,
        destination: oversightAssessmentDetailsUrl(assessmentId),
      },
      successNavigation: {
        destination: oversightAssessmentDetailsUrl(assessmentId),
        isReplace: true,
      },
    };
  };

  const onDismiss = (isSaved?: boolean) => {
    if (!isSaved) {
      navigate(-1);
    }
  };

  return (
    <OversightAssessmentForm
      onSave={onSave}
      onDismiss={onDismiss}
      publishToolbarState={objectToolbarEnabled}
      publishActiveForm={formPrefillEnabled}
      renderTemplate={(renderProps) => <PageWrapper {...renderProps} />}
    />
  );
};

export default OversightAssessmentCreateTab;
